import React, { useState, useEffect } from "react";
import FormStep from "./FormStep";
import Input from "../components/form/Input";
import EmailInput from "../components/form/EmailInput";
import RadioGroup from "../components/form/RadioGroup";
import FirmTypeSelect from "../components/form/FirmTypeSelect";

// Configuration: Set to true to make firm type required, false to make it optional
const FIRM_TYPE_REQUIRED = true;

const YourDetails = ({ nextStep, prevStep, setFormData, formData, bookingForOptions = [] }) => {
	const [errors, setErrors] = useState({});
	const [wasValidated, setWasValidated] = useState(false);
	const [emailValidation, setEmailValidation] = useState({ isChecking: false, hasError: false });
	const [pendingSubmit, setPendingSubmit] = useState(false);
	
	// Get event data for email checking
	const eventData = formData.event || {};
	const shouldCheckDuplicates = formData.bookingFor === "Myself" || formData.bookingFor === "Myself & others";
	
	// Clear email checking errors once validation completes and handle pending submit
	useEffect(() => {
		// If we were checking and now we're not, and the form was previously validated, re-validate
		if (!emailValidation.isChecking && wasValidated && errors.email && errors.email.isChecking) {
			// Clear the checking error and re-validate
			setTimeout(() => {
				const isValid = validateForm();
				// If we have a pending submit and form is now valid, proceed automatically
				if (pendingSubmit && isValid) {
					setPendingSubmit(false);
					proceedToNextStep();
				}
			}, 0);
		}
	}, [emailValidation.isChecking, wasValidated]);
	
	const proceedToNextStep = () => {
		// Update invoice company name if it's empty but we have a company name
		if (formData.company && (!formData.invoiceDetails || !formData.invoiceDetails.companyName)) {
			const updatedInvoiceDetails = {
				...(formData.invoiceDetails || {}),
				companyName: formData.company
			};
			
			setFormData(prev => ({
				...prev,
				invoiceDetails: updatedInvoiceDetails
			}));
		}
		
		nextStep();
	};
	
	const validateForm = () => {
		const newErrors = {};
		
		// Email validation
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		
		// Check if email is still being validated or has duplicate error
		if (formData.bookingFor !== "Others") {
			if (emailValidation.isChecking) {
				// Show a special error message while checking
				newErrors.email = {message: 'Checking email availability, please wait...', isChecking: true};
			} else if (emailValidation.hasError) {
				// The EmailInput component will show its own duplicate error message
				// We don't need to add an error here, just prevent submission
			} else if (!formData.email || !emailRegex.test(formData.email)) {
				// Only show format error if not checking and no duplicate error
				newErrors.email = {message: 'Please enter a valid email address'};
			}
			// If email is valid and not checking and no duplicate error, don't add any error
		} else {
			// For "Others" booking type, just check format
		if (!formData.email || !emailRegex.test(formData.email)) {
			newErrors.email = {message: 'Please enter a valid email address'};
			}
		}
		
		// Phone validation - allow numbers, spaces, plus, and hyphens
		const phoneRegex = /^[0-9\s\-\+\(\)]+$/;
		const digitsOnly = formData.mobile.replace(/[^\d]/g, '');
		if (!formData.mobile) {
			newErrors.mobile = {message: 'Telephone number is required'};
		} else if (!phoneRegex.test(formData.mobile)) {
			newErrors.mobile = {message: 'Please enter a valid telephone number'};
		} else if (digitsOnly.length < 11) {
			newErrors.mobile = {message: 'Please enter a valid telephone number'};
		}
		
		// Required fields validation
		if (!formData.firstName?.trim()) newErrors.firstName = {message: 'First name is required'};
		if (!formData.lastName?.trim()) newErrors.lastName = {message: 'Last name is required'};
		if (!formData.bookingFor) newErrors.bookingFor = { message: 'Please select who you are booking for' };
		
		if (!formData.jobTitle?.trim()) newErrors.jobTitle = { message: 'Please enter your job title' };
		
		// Company validation - required for all booking types
		if (!formData.company?.trim()) {
			newErrors.company = {message: 'Company name is required'};
		}
		
		// Firm type validation - only if required AND firm types are configured (field is visible)
		if (FIRM_TYPE_REQUIRED) {
			const firmTypesConfigured = (() => {
				const formElement = document.getElementById('lde-booking-form');
				if (formElement && formElement.dataset.firmTypes) {
					try {
						const types = JSON.parse(formElement.dataset.firmTypes);
						return types && types.length > 0;
					} catch (e) {
						return false;
					}
				}
				return false;
			})();
			
			if (firmTypesConfigured && !formData.firmType?.trim()) {
				newErrors.firmType = {message: 'Please select a firm type'};
			}
		}
		
		setErrors(newErrors);
		
		// Check if we should prevent submission due to email validation
		const hasRegularErrors = Object.keys(newErrors).some(key => !newErrors[key]?.isChecking);
		const isEmailValidationBlocking = formData.bookingFor !== "Others" && (emailValidation.isChecking || emailValidation.hasError);
		
		return !hasRegularErrors && !isEmailValidationBlocking;
	};

	const handleChange = (e) => {
		setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
		
		// If the form has been submitted once, validate on each change
		if (wasValidated) {
			setTimeout(() => {
				validateForm();
			}, 0);
		}
		
		// Clear error when user types
		if (errors[e.target.name]) {
			setErrors(prev => ({ ...prev, [e.target.name]: null }));
		}
	};
	
	const handleBlur = (e) => {
		// Validate and clear errors on blur
		if (wasValidated) {
			// Create temporary form data with the current field's value
			const updatedFormData = {...formData, [e.target.name]: e.target.value};
			
			// Check only this specific field
			const fieldError = validateField(e.target.name, updatedFormData);
			
			// Update only this field's error state
			if (fieldError) {
				setErrors(prev => ({ ...prev, [e.target.name]: fieldError }));
			} else {
				setErrors(prev => ({ ...prev, [e.target.name]: null }));
			}
		}
	};
	
	// Validate a single field
	const validateField = (fieldName, data = formData) => {
		switch(fieldName) {
			case 'email':
				const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
				if (!data.email || !emailRegex.test(data.email)) {
					return {message: 'Please enter a valid email address'};
				}
				break;
			case 'mobile':
				const phoneRegex = /^[0-9\s\-\+\(\)]+$/;
				const digitsOnly = data.mobile.replace(/[^\d]/g, '');
				if (!data.mobile) {
					return {message: 'Telephone number is required'};
				} else if (!phoneRegex.test(data.mobile)) {
					return {message: 'Please enter a valid telephone number'};
				} else if (digitsOnly.length < 11) {
					return {message: 'Please enter a valid telephone number'};
				}
				break;
			case 'firstName':
				if (!data.firstName?.trim()) return {message: 'First name is required'};
				break;
			case 'lastName':
				if (!data.lastName?.trim()) return {message: 'Last name is required'};
				break;
			case 'company':
				if (!data.company?.trim()) return {message: 'Company name is required'};
				break;
			case 'bookingFor':
				if (!data.bookingFor) return {message: 'Please select who you are booking for'};
				break;
			case 'firmType':
				// Only validate if required AND firm types are configured
				if (FIRM_TYPE_REQUIRED) {
					const formElement = document.getElementById('lde-booking-form');
					if (formElement && formElement.dataset.firmTypes) {
						try {
							const types = JSON.parse(formElement.dataset.firmTypes);
							if (types && types.length > 0 && !data.firmType?.trim()) {
								return {message: 'Please select a firm type'};
							}
						} catch (e) {
							// If parsing fails, don't require validation
						}
					}
				}
				break;
			default:
				return null;
		}
		return null;
	};

	const handleNext = () => {
		setWasValidated(true);
		
		// If email is currently being checked, remember that we want to submit
		if (emailValidation.isChecking) {
			setPendingSubmit(true);
			return;
		}
		
		// If form is valid, proceed immediately
		if (validateForm()) {
			proceedToNextStep();
		}
	};
	
	const handleEmailValidationChange = (validationState) => {
		setEmailValidation(validationState);
	};
	
	return (
		<FormStep 
			title="Your Details" 
			onBack={null} 
			onNext={handleNext}
			isNextDisabled={false} // Allow clicking to trigger validation error display
		>
			<div className="form-row booking-type">
				<RadioGroup
					id="bookingFor"
					label="Who are you booking for?"
					onChange={handleChange}
					onBlur={handleBlur}
					value={formData.bookingFor}
					options={bookingForOptions}
					error={errors.bookingFor}
					required={true}
				/>
			</div>
			
			<div className="form-row">
				<div className="flex">
					<div className="col-6">
						<Input 
							id="firstName" 
							label="Your First Name" 
							autocomplete="given-name"
							onChange={handleChange}
							onBlur={handleBlur}
							value={formData.firstName}
							error={errors.firstName}
							required={true}
						/>
					</div>
					<div className="col-6">
						<Input 
							id="lastName" 
							label="Your Last Name" 
							autocomplete="family-name"
							onChange={handleChange}
							onBlur={handleBlur}
							value={formData.lastName}
							error={errors.lastName}
							required={true}
						/>
					</div>
				</div>
			</div>
			<div className="form-row">
				<Input 
					id="company" 
					label="Company Name" 
					autocomplete="organization"
					onChange={handleChange}
					onBlur={handleBlur}
					value={formData.company || ""}
					error={errors.company}
					required={true}
				/>
			</div>
			<div className="form-row">
				<FirmTypeSelect
					id="firmType"
					label="Type of Firm"
					onChange={handleChange}
					onBlur={handleBlur}
					value={formData.firmType || ""}
					error={errors.firmType}
					required={FIRM_TYPE_REQUIRED}
				/>
			</div>
			<div className="form-row">
				<Input 
					id="jobTitle" 
					label="Your Job Title" 
					autocomplete="organization-title"
					onChange={handleChange}
					onBlur={handleBlur}
					value={formData.jobTitle || ""}
					error={errors.jobTitle}
					required={true}
				/>
			</div>
			<div className="form-row">
				<EmailInput 
					id="email" 	
					label="Your Email" 
					autocomplete="email"
					onChange={handleChange}
					onBlur={handleBlur}
					value={formData.email}
					error={errors.email}
					required={true}
					checkDuplicates={formData.bookingFor === "Myself" || formData.bookingFor === "Myself & others"}
					eventId={formData.event?.id || formData.event?.event_id}
					blogId={formData.event?.blogId || formData.event?.blog_id}
					existingEmails={formData.delegates ? formData.delegates.map(d => d.email.toLowerCase()) : []}
					onValidationChange={handleEmailValidationChange}
				/>
			</div>
			<div className="form-row">
				<Input 
					id="mobile" 
					label="Your Mobile Number" 
					autocomplete="tel"
					onChange={handleChange}
					onBlur={handleBlur}
					value={formData.mobile}
					error={errors.mobile}
					required={true}
				/>
			</div>
		</FormStep>
	);
};

export default YourDetails;
